import { EExternalLinkType, TExternalLink } from "@/types/TExternalLink";
import { EBrandName } from "@/types/TInfoBox";
import { PHONE_REGEX } from "./constants";

/**
 * Extracts phone numbers, email addresses, and URLs from a text message
 * @param message - The text message to extract links from
 * @returns Array of external links found in the message
 */
export default function extractExternalLinks(message: string): TExternalLink[] {
  const links: TExternalLink[] = [];

  // Email regex - matches standard email formats
  // Examples: user@domain.com, test.email@example.org
  const emailRegex = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g;
  
  // URL regex - matches http/https URLs, www domains, brand domains, and common domains
  // Examples: https://example.com, http://test.de, www.example.com, domcura.de, nordvers.com, innoas.de
  const brandNames = Object.values(EBrandName).join('|');
  const urlRegex = new RegExp(`(?:https?:\\/\\/(?:[a-zA-Z0-9][-a-zA-Z0-9@:%._\\+~#=]{0,255}[a-zA-Z0-9]\\.[a-zA-Z0-9()]{1,6}\\b(?:[-a-zA-Z0-9()@:%_\\+.~#?&=\\/]*[a-zA-Z0-9])?)|(?:www\\.(?:[a-zA-Z0-9][-a-zA-Z0-9@:%._\\+~#=]{0,255}[a-zA-Z0-9]\\.[a-zA-Z0-9()]{1,6}\\b(?:[-a-zA-Z0-9()@:%_\\+.~#?&=\\/]*[a-zA-Z0-9])?))|(?:(?:${brandNames})\\.(?:de|com)(?:\\/[-a-zA-Z0-9()@:%_\\+.~#?&=\\/]*)?\\b)|(?:[a-zA-Z0-9][-a-zA-Z0-9]{0,61}[a-zA-Z0-9]\\.(?:com|de|org|net|info)(?:\\/[-a-zA-Z0-9()@:%_\\+.~#?&=\\/]*)?\\b))`, 'g');
    // Extract phone numbers
  const phoneMatches = message.match(PHONE_REGEX);
  if (phoneMatches) {
    phoneMatches.forEach((match) => {
      // Clean up the phone number (remove excessive spaces, format, and trailing punctuation)
      const cleanPhone = match.replace(/\s+/g, ' ').replace(/[.,;!?]+$/, '').trim();
      
      // Basic validation: should contain at least 7 digits
      const digitCount = (cleanPhone.match(/\d/g) || []).length;
      if (digitCount >= 7 && digitCount <= 15) {
        links.push({
          type: EExternalLinkType.phone,
          value: cleanPhone,
          originalText: match
        });
      }
    });
  }
  
  // Extract email addresses
  const emailMatches = message.match(emailRegex);
  if (emailMatches) {
    emailMatches.forEach((match) => {
      links.push({
        type: EExternalLinkType.email,
        value: match,
      });
    });
  }
  
  // Extract URLs
  const urlMatches = [...message.matchAll(urlRegex)];
  if (urlMatches.length > 0) {
    urlMatches.forEach((matchResult) => {
      const match = matchResult[0];
      const matchIndex = matchResult.index!;
      
      // Clean up URL (remove trailing punctuation)
      let cleanUrl = match.replace(/[.,;!?]+$/, '');
      const originalText = cleanUrl; // Store the original matched text
      
      // Check if this exact URL match is part of an email address
      // Check if there's an @ symbol immediately before this match
      if (matchIndex > 0) {
        const charBefore = message[matchIndex - 1];
        if (charBefore === '@') {
          return; // Skip this match as it's the domain part of an email
        }
      }
      
      // Ensure URL has protocol
      if (!cleanUrl.startsWith('http://') && !cleanUrl.startsWith('https://')) {
        cleanUrl = 'https://' + cleanUrl;
      }
      
      links.push({
        type: EExternalLinkType.url,
        value: cleanUrl,
        originalText: originalText
      });
    });
  }
  
  // Remove duplicates based on type and value combination
  const uniqueLinks = links.filter((link, index, self) => 
    index === self.findIndex(l => l.type === link.type && l.value === link.value)
  );
  
  return uniqueLinks;
}
